import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatedThemeToggler } from './ui/AnimatedThemeToggler';

/* ─── Animated Hamburger ↔ X icon ─────────────────────────────────────────── */
const HamburgerIcon = ({ isOpen }) => (
  <div
    className="relative flex flex-col justify-center items-center w-7 h-7 cursor-pointer"
    aria-hidden="true"
  >
    {/* Top bar */}
    <span
      className="bg-foreground"
      style={{
        display: 'block',
        position: 'absolute',
        width: '24px',
        height: '2px',
        borderRadius: '2px',
        transition: 'transform 0.35s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.25s ease',
        transform: isOpen ? 'translateY(0px) rotate(45deg)' : 'translateY(-7px) rotate(0deg)',
      }}
    />
    {/* Middle bar */}
    <span
      className="bg-foreground"
      style={{
        display: 'block',
        position: 'absolute',
        width: '24px',
        height: '2px',
        borderRadius: '2px',
        transition: 'opacity 0.2s ease, transform 0.35s cubic-bezier(0.23, 1, 0.32, 1)',
        opacity: isOpen ? 0 : 1,
        transform: isOpen ? 'scaleX(0)' : 'scaleX(1)',
      }}
    />
    {/* Bottom bar */}
    <span
      className="bg-foreground"
      style={{
        display: 'block',
        position: 'absolute',
        width: '24px',
        height: '2px',
        borderRadius: '2px',
        transition: 'transform 0.35s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.25s ease',
        transform: isOpen ? 'translateY(0px) rotate(-45deg)' : 'translateY(7px) rotate(0deg)',
      }}
    />
  </div>
);

/* ─── Header ───────────────────────────────────────────────────────────────── */
export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showContactPopup, setShowContactPopup] = useState(false);
  const contactPopupRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e) => {
      if (contactPopupRef.current && !contactPopupRef.current.contains(e.target))
        setShowContactPopup(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  // Handle hash scrolling on navigation/load
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(id);
        }, 100);
      }
    } else if (location.pathname === '/') {
      setActiveSection('home');
    }
  }, [location]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/#' + sectionId);
      setIsMobileMenuOpen(false);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home', type: 'section' },
    { label: 'About', id: 'about', type: 'section' },
    { label: 'Services', id: 'services', type: 'section' },
    { label: 'Blog', id: 'blog', type: 'link', path: '/blog' },
    { label: 'FAQ', id: 'faq', type: 'section' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${isScrolled || location.pathname !== '/'
          ? 'bg-background/80 backdrop-blur-xl border-b border-foreground/5'
          : 'bg-transparent'
          }`}
      >
        <nav className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <div
              onClick={() => scrollToSection('home')}
              className="text-2xl font-light tracking-tight group cursor-pointer"
            >
              <span className="text-foreground group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300">
                Aadarsh
              </span>
              <span className="text-purple-600 dark:text-purple-500 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors duration-300">
                .
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                item.type === 'section' ? (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`text-sm font-light tracking-wide transition-colors duration-300 hover:-translate-y-0.5 transform ${activeSection === item.id && location.pathname === '/' ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground/70 hover:text-yellow-600 dark:hover:text-yellow-400'}`}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={`text-sm font-light tracking-wide transition-colors duration-300 hover:-translate-y-0.5 transform ${location.pathname.startsWith(item.path) ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground/70 hover:text-yellow-600 dark:hover:text-yellow-400'}`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              {/* Desktop Contact + Theme Toggler */}
              <div className="flex items-center space-x-4">
                <div className="relative" ref={contactPopupRef}>
                  <button
                    onClick={() => setShowContactPopup(p => !p)}
                    className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-yellow-500 hover:to-yellow-600 text-white text-sm font-medium rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
                  >
                    Contact
                  </button>

                  {/* QR Dropdown — desktop only */}
                  {showContactPopup && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 12px)', right: 0,
                      width: 260, zIndex: 200,
                      background: 'hsl(var(--background) / 0.92)', backdropFilter: 'blur(20px)',
                      border: '1px solid hsl(var(--foreground) / 0.1)', borderRadius: 20,
                      padding: '20px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
                      animation: 'fadeSlideDown 0.2s ease',
                    }}>
                      {/* QR code */}
                      <p className="hidden md:block text-center text-[10px] text-foreground dark:text-foreground/40 tracking-[0.15em] uppercase mb-2.5">
                        Scan to Connect From mobile
                      </p>
                      {/* QR code — white background always, invert in dark mode */}
                      <div style={{ textAlign: 'center', marginBottom: 14 }}>
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Faadarshrajesh.page%2Fcard&bgcolor=ffffff&color=000000&margin=8&qzone=1`}
                          alt="QR code"
                          className="dark:invert"
                          style={{ width: 160, height: 160, borderRadius: 12, border: '1px solid hsl(var(--foreground) / 0.12)', display: 'inline-block' }}
                        />
                      </div>

                      {/* Divider */}
                      <div style={{ height: 1, background: 'hsl(var(--foreground) / 0.07)', margin: '12px 0' }} />

                      {/* Social mini icons — glass style (ProfileCard style, opposite per theme) */}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
                        {[
                          {
                            href: 'https://www.linkedin.com/in/aadarshrajesh/', label: 'LinkedIn',
                            svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                          },
                          {
                            href: 'https://www.instagram.com/aadarshrajeshkumar/', label: 'Instagram',
                            svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                          },
                          {
                            href: 'https://wa.me/+918921158382', label: 'WhatsApp',
                            svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                          },
                          {
                            href: (typeof navigator !== 'undefined' && (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 1024))
                              ? 'mailto:aadarshkk45@gmail.com?subject=Hi%2C%20I%20recently%20came%20across%20your%20website%20and%20wanted%20to%20connect.'
                              : 'https://mail.google.com/mail/?view=cm&fs=1&to=aadarshkk45@gmail.com&su=Hi%2C%20I%20recently%20came%20across%20your%20website%20and%20wanted%20to%20connect.',
                            label: 'Gmail',
                            svg: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" /></svg>
                          },
                        ].map(s => {
                          const isMailto = s.href.startsWith('mailto:');
                          return (
                            <a key={s.label} href={s.href}
                              target={isMailto ? "_self" : "_blank"}
                              rel="noopener noreferrer"
                              title={s.label}
                              className="w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-md transition-all duration-300 hover:scale-110
                                dark:bg-white/5 dark:border dark:border-white/10 dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-yellow-400 dark:hover:border-white/20
                                bg-black/5 border border-black/10 text-black/40 hover:bg-black/10 hover:text-purple-600 hover:border-black/20"
                            >
                              {s.svg}
                            </a>
                          );
                        })}
                      </div>

                      {/* Download Resume */}
                      <a href={`${window.location.origin}/resume.pdf`} download="resume.pdf"
                        className="group flex items-center justify-center gap-2 w-full py-2.5 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
                        style={{
                          background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(234,179,8,0.1))',
                          border: '1px solid rgba(139,92,246,0.25)',
                          textDecoration: 'none',
                        }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-600 dark:text-yellow-400">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span className="text-foreground group-hover:text-purple-700 dark:group-hover:text-yellow-400 text-xs font-bold tracking-wider uppercase transition-colors duration-300">
                          Download Resume
                        </span>
                      </a>

                      <style>{`
                      @keyframes fadeSlideDown {
                        from{opacity:0;transform:translateY(-8px)}
                        to{opacity:1;transform:translateY(0)}
                      }
                    `}</style>
                    </div>
                  )}
                </div>
                <AnimatedThemeToggler />
              </div>
            </div>

            {/* Mobile Hamburger — always rendered, morphs to X */}
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="md:hidden p-2 rounded-lg hover:bg-foreground/5 transition-colors duration-300 z-[60] relative"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <HamburgerIcon isOpen={isMobileMenuOpen} />
            </button>

          </div>
        </nav>
      </header>

      {/* Full-Screen Mobile Menu Overlay */}
      <div
        className="fixed inset-0 h-[100dvh] md:hidden z-[55] bg-background"
        style={{
          opacity: isMobileMenuOpen ? 1 : 0,
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
          transition: 'opacity 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      >
        {/* Centered Nav Items */}
        <div className="flex flex-col items-center justify-center h-[100dvh] gap-1">
          {navItems.map((item, index) => (
            item.type === 'section' ? (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="group relative py-4 px-8 text-center"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 0.4s ease, transform 0.4s ease`,
                  transitionDelay: isMobileMenuOpen ? `${index * 65 + 80}ms` : '0ms',
                }}
              >
                <span
                  className={`block text-3xl font-bold tracking-wide transition-colors duration-300 ${activeSection === item.id && location.pathname === '/' ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground hover:text-foreground/80'
                    }`}
                >
                  {item.label}
                </span>
                {/* Active / hover underline */}
                <span
                  className={`block h-[3px] mt-1 rounded-full transition-all duration-300 ${activeSection === item.id && location.pathname === '/'
                    ? 'bg-purple-600 dark:bg-purple-500 w-full'
                    : 'bg-transparent w-0 group-hover:w-full group-hover:bg-purple-500/50'
                    }`}
                />
              </button>
            ) : (
              <Link
                key={item.id}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="group relative py-4 px-8 text-center"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(16px)',
                  transition: `opacity 0.4s ease, transform 0.4s ease`,
                  transitionDelay: isMobileMenuOpen ? `${index * 65 + 80}ms` : '0ms',
                }}
              >
                <span
                  className={`block text-3xl font-bold tracking-wide transition-colors duration-300 ${location.pathname.startsWith(item.path) ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground hover:text-foreground/80'
                    }`}
                >
                  {item.label}
                </span>
                {/* Active / hover underline */}
                <span
                  className={`block h-[3px] mt-1 rounded-full transition-all duration-300 ${location.pathname.startsWith(item.path)
                    ? 'bg-purple-600 dark:bg-purple-500 w-full'
                    : 'bg-transparent w-0 group-hover:w-full group-hover:bg-purple-500/50'
                    }`}
                />
              </Link>
            )
          ))}

          {/* Contact button + Theme Toggler */}
          <div className="flex flex-col items-center gap-6 mt-6">
            <button
              onClick={() => scrollToSection('contact')}
              className="px-10 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-yellow-500 hover:to-yellow-600 text-white text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                transitionDelay: isMobileMenuOpen ? `${navItems.length * 65 + 80}ms` : '0ms',
              }}
            >
              Contact
            </button>
            <div
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(16px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
                transitionDelay: isMobileMenuOpen ? `${(navItems.length + 1) * 65 + 80}ms` : '0ms',
              }}
            >
              <AnimatedThemeToggler />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
